
import React, { useEffect, useRef } from 'react';
import { AppSection } from '../types';

// Define props to match App.tsx usage
interface SolutionsProps {
  onNavigate: (section: AppSection) => void;
  onContactClick: () => void;
}

const Solutions: React.FC<SolutionsProps> = ({ onNavigate, onContactClick }) => {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('active');
        }
      });
    }, { threshold: 0.1 });

    const elements = sectionRef.current?.querySelectorAll('.reveal');
    elements?.forEach(el => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const methodology = [
    { title: "Discovery & Audit", desc: "A technical and business analysis of your current stack and organizational goals." },
    { title: "Architectural Blueprint", desc: "Design of the 'To-Be' state with focus on security, scalability, and cost." },
    { title: "Managed Execution", desc: "Delivery led by senior program managers using Hunargrid vetted squads." },
    { title: "Lifecycle Transfer", desc: "Comprehensive documentation and team training for seamless handoff." }
  ];

  return (
    <div className="bg-white">
      <section className="bg-[#020617] py-24 md:py-32 text-white relative overflow-hidden">
        <div className="absolute inset-0 grid-bg-dark opacity-20"></div>
        <div className="absolute -top-24 -right-24 w-96 h-96 bg-[#0091da]/10 rounded-full blur-[100px]"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-3xl animate-in fade-in slide-in-from-bottom-8 duration-1000">
            <div className="mono text-xs text-[#0091da] font-bold mb-6 tracking-widest uppercase">// Engine 01: Project Solutions</div>
            <h1 className="text-5xl md:text-8xl font-bold mb-8 tracking-tighter leading-[0.9]">Strategic Delivery Outcomes</h1>
            <p className="text-lg md:text-xl text-slate-400 font-light leading-relaxed max-w-2xl">
              We own the outcome. From technical debt modernization to greenfield product launches, Hunargrid provides the strategic oversight to move the needle.
            </p>
          </div>
        </div>
      </section>

      <section ref={sectionRef} className="py-20 md:py-24 border-b border-slate-100 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-12">
            {methodology.map((step, i) => (
              <div 
                key={i} 
                className="reveal group p-8 rounded-sm hover:bg-slate-50 transition-all border-l-2 border-transparent hover:border-[#0091da]"
                style={{ transitionDelay: `${i * 150}ms` }}
              >
                <div className="mono text-xs text-slate-300 mb-6 group-hover:text-[#0091da] transition-colors font-bold">PHASE_0{i+1}</div>
                <h3 className="text-xl font-bold text-slate-950 mb-4">{step.title}</h3>
                <p className="text-slate-500 text-sm leading-relaxed font-light">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-slate-50 relative overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-40"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="flex flex-col lg:flex-row gap-16 md:gap-24 items-center">
            <div className="lg:w-1/2 reveal">
               <div className="mono text-xs text-[#0091da] font-bold mb-4 uppercase tracking-widest">// Featured Case Study</div>
               <h2 className="text-4xl md:text-5xl font-bold text-slate-950 mb-8 tracking-tight leading-tight">Cloud-Native Transformation for Chicago FinTech</h2>
               <p className="text-base md:text-lg text-slate-600 mb-10 font-light leading-relaxed">
                 Reduced operational overhead by 40% while increasing deployment frequency from monthly to daily using a specialized Hunargrid DevOps squad.
               </p>
               {/* Fixed: Use onNavigate to drive traffic to case studies */}
               <button 
                onClick={() => onNavigate(AppSection.CaseStudies)}
                className="bg-slate-900 text-white px-10 py-5 text-xs font-bold uppercase tracking-widest hover:bg-[#0091da] transition-all rounded-sm shadow-xl shadow-slate-900/10"
               >
                 Read Full Study
               </button>
            </div>
            <div className="lg:w-1/2 w-full aspect-video reveal bg-white border border-slate-200 rounded-sm shadow-2xl relative overflow-hidden group" style={{ transitionDelay: '300ms' }}>
               <div className="absolute inset-0 grid-bg opacity-20 group-hover:opacity-40 transition-opacity"></div>
               <div className="absolute inset-0 flex items-center justify-center p-12">
                  <div className="w-full h-full border border-dashed border-[#0091da]/30 rounded flex items-center justify-center">
                    <div className="text-center">
                      <div className="mono text-[10px] text-[#0091da] uppercase mb-4 tracking-[0.3em]">Technical Architecture</div>
                      <div className="w-48 h-1 bg-gradient-to-r from-transparent via-[#0091da]/20 to-transparent"></div>
                    </div>
                  </div>
               </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Solutions;
